import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { memoKeys } from '../api/memoAPI';
import {
  fetchMemos, fetchMemo,
  syncMemo, triggerAnalysis
} from '../api/memoAPI';
import { UIMemo } from '../../model/UiShapes';
import { handleError, useErrorHandler } from '../managers/errorHandler';

// Memo列表查询 - 统一命名规范
export const useMemoListQuery = (options?: { enabled?: boolean }) => {
  const queryResult = useQuery({
    queryKey: memoKeys.list(),
    queryFn: async () => {
      try {
        const response = await fetchMemos();
        return response || [];
      } catch (error) {
        handleError(error, { context: '获取memos失败' });
        throw error;
      }
    },
    enabled: options?.enabled !== undefined ? options.enabled : true,
    staleTime: 5 * 60 * 1000, // 5分钟
    gcTime: 10 * 60 * 1000, // 10分钟
  });

  return queryResult;
};

// 单个Memo查询
export const useMemoDetailQuery = (memoId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: memoKeys.memo(memoId),
    queryFn: async () => {
      const response = await fetchMemo(memoId);
      return response || null;
    },
    enabled: (options?.enabled !== undefined ? options.enabled : !!memoId),
    staleTime: 2 * 60 * 1000,
  });
};



/**
 * Memo操作mutation hooks - 简化版本
 */

// 创建Memo
export const useCreateMemoMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (memo: any) => syncMemo(memo),
    onMutate: async (newMemo) => {
      // 乐观更新：更新React Query缓存
      const listQueryKey = memoKeys.list();
      const currentMemos = queryClient.getQueryData<UIMemo[]>(listQueryKey) || [];
      
      // 确保memo不在列表中（避免重复添加）
      if (!currentMemos.some(m => m.guid === newMemo.guid)) {
        const updatedMemos = [...currentMemos, newMemo];
        queryClient.setQueryData(listQueryKey, updatedMemos);
      }
      
      // 更新单个memo的缓存
      queryClient.setQueryData(memoKeys.memo(newMemo.guid), newMemo);
      
      return { previousMemos: currentMemos };
    },
    onSuccess: (data, variables) => {
      // 只失效单个memo的缓存，避免过度触发列表查询
      queryClient.invalidateQueries({ queryKey: memoKeys.memo(variables.guid) });
      
      // 选择性更新列表缓存，而不是完全失效
      const listQueryKey = memoKeys.list();
      const currentMemos = queryClient.getQueryData<UIMemo[]>(listQueryKey) || [];
      
      // 如果新memo不在当前列表中，则更新列表缓存
      if (!currentMemos.some(m => m.guid === variables.guid)) {
        const updatedMemos = [...currentMemos, { ...variables, ...data }];
        queryClient.setQueryData(listQueryKey, updatedMemos);
      }
    },
    onError: (error, variables, context) => {
      // 错误回滚：恢复之前的缓存状态
      if (context?.previousMemos) {
        queryClient.setQueryData(memoKeys.list(), context.previousMemos);
        queryClient.setQueryData(memoKeys.memo(variables.guid), undefined);
      }
      
      handleError(error, { context: '创建Memo失败' });
    },
  });
};

// 更新Memo
export const useUpdateMemoMutation = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  
  return useMutation({
    mutationFn: (memo: any) => syncMemo(memo),
    onSuccess: (data, variables) => {
      // 只失效和更新单个memo的缓存，避免过度触发列表查询
      queryClient.invalidateQueries({ queryKey: memoKeys.memo(variables.guid) });
      
      // 选择性更新列表缓存
      const listQueryKey = memoKeys.list();
      const currentMemos = queryClient.getQueryData<UIMemo[]>(listQueryKey) || [];
      
      // 更新列表中的对应memo
      const updatedMemos = currentMemos.map(m => 
        m.guid === variables.guid ? { ...m, ...variables, ...data } : m
      );
      queryClient.setQueryData(listQueryKey, updatedMemos);
    },
    onError: (error) => {
      handleError(error, { context: '更新Memo失败' });
    },
  });
};



// Memo分析
export const useTriggerAnalysisMutation = () => {
  return useMutation({
    mutationFn: (request: any) => triggerAnalysis(request),
  });
};